import type { PropsWithChildren } from "react";
import { useCallback, useRef } from "react";
import type { AnyRecord } from "@entrilia/shared/types";
import type { Props } from "./FilterContext";
import { FilterContext } from "./FilterContext";
import type { SelectOption } from "./filterTypes";

export const FilterContextProvider = <R extends AnyRecord>({
  filterState,
  setFilterState,
  allRowsForSelectOptions,
  getSelectOptionsForFilter,
  children,
}: PropsWithChildren<Props<R>>) => {
  const selectOptionsCacheRef = useRef<Map<string, SelectOption[]>>(new Map<string, SelectOption[]>());

  const getAsyncSelectOptions = useCallback(
    async (filterId: string) => {
      const cachedOptions = selectOptionsCacheRef.current.get(filterId);
      if (cachedOptions !== undefined) {
        return cachedOptions;
      }

      if (getSelectOptionsForFilter === undefined) {
        return [];
      }

      const options = await getSelectOptionsForFilter(filterId);
      selectOptionsCacheRef.current.set(filterId, options);
      return options;
    },
    [getSelectOptionsForFilter]
  );

  return (
    <FilterContext
      value={{
        filterState,
        setFilterState,
        allRowsForSelectOptions,
        getAsyncSelectOptions,
      }}
    >
      {children}
    </FilterContext>
  );
};
